<template>
  <div class="flex w-full">
    <server-table
      class="w-full text-center groovemail-common-import-leads-table"
      :url="importSourceUrl"
      ref="serverTable"
      tableRef="importTable"
      :tableTitle="importTableTitle"
      :slotData="importListingTableSlotData"
      :tableColumns="importTableColumns"
      :tableOptions="importTableOptions"
      :emptyStateData="showEmptyState ? emptyStateData : null"
      @loaded="hideLoader()"
      @loading="showLoader()"
    >
    </server-table>
  </div>
</template>

<script>
import ServerTable from '@groovepages/gd-ui-kit/components/tables/ServerTable.vue';
import { adminUrls } from '@/utils/urls.js';
import monthsMixin from '@/mixin/monthsMixin';

export default {
  props: {
    importSourceUrl: {
      required: true,
    },
    importTableColumns: {
      required: true,
    },
    importTableOptions: {
      required: true,
    },
    importTableTitle: {
      required: false,
    },
    importLeadType: {
      required: false,
    },
    percentSwitch: {
      required: false,
    },
    showEmptyState: {
      required: false,
    },
  },

  mixins: [monthsMixin],

  data() {
    return {
      tableKey: 1,
	    downloadAllLeadsUrl: adminUrls.importManagementUrl.downloadAllLeads,
      downloadAllGoodUrl: adminUrls.importManagementUrl.downloadGoodLeads,
      downloadAllBadUrl: adminUrls.importManagementUrl.downloadBadLeads,
      emptyStateData: {
        emptyStateHeading: 'frontend_groovemail_imported_leads_listing_page_title',
        emptyStateSubHeading: 'frontend_groovemail_imported_leads_have_no_data',
      },
    };
  },

  components: {
    ServerTable
  },

  computed: {
    /**	
     * Returns slot data for import listing tables.	
     * @return {array} details for slot columns for import listing tables.	
    */
    importListingTableSlotData() {
      return [
        {
          slotName: 'appr_rej_count',
          slotType: 'fraction',
          value: (props) => {
            return {
              first: props.row.approved_count,
              second: props.row.rejected_count
            };
          },
          firstCallback: (props) => this.showImportsForThisUser('approvedFiles', props.row.user_email),
          secondCallback: (props) => this.showImportsForThisUser('rejectedFiles', props.row.user_email),
        },
        {
          slotName: 'user_email',
          slotType: 'computed',
          classes: () => {
 	          return '';
          },
          value: (props) => {
            return props.row.user_email
              ? props.row.user_email
              : this.$t('frontend_groovemail_not_available');
          },
        },
        {
          slotName: 'csv_name',
          slotType: 'computed',
          classes: () => {
 	          return '';
          },
          value: (props) => {
            return props.row.csv_name
              ? props.row.csv_name
              : this.$t('frontend_groovemail_not_available');
          },
        },
        {
          slotName: 'actioned_by',
          slotType: 'computed',
          classes: () => {
 	          return '';
          },
          value: (props) => {
            return props.row.actioned_by
              ? props.row.actioned_by
              : this.$t('frontend_groovemail_not_available');
          },
        },
        {
          slotName: 'approved',
          slotType: 'computed',
          classes: (props) => {
            if (props.row.approved === 0 || props.row.approved === 400) {
              return "inline border border-warning px-5 py-1 rounded-md text-warning text-xs uppercase";
            }
            if (props.row.approved === 3) {
              return "inline border border-danger px-5 py-1 rounded-md text-danger text-xs uppercase";
            }
            return "inline border border-success px-5 py-1 rounded-md text-success text-xs uppercase";
          },
          value: (props) => {
            if (props.row.approved === 0 || props.row.approved === 400) {
              return this.$t(
                'frontend_groovemail_import_user_listing_pending_status'
              );
            }
            if (props.row.approved === 3) {
              return this.$t(
                'frontend_groovemail_import_user_listing_rejected_status'
              );
            }
            return this.$t(
              'frontend_groovemail_import_user_listing_approved_status'
            );
          },
        },
        {
          slotName: 'total_leads',
          slotType: 'computed',
          classes: () => {
 	          return '';
          },
          value: (props) => {
            return props.row.total_leads ? props.row.total_leads : `0`;
          },
        },
        {
          slotName: 'block_lead',
          slotType: 'computed',
          classes: () => {
 	          return '';
          },
          value: (props) => {
            if (this.percentSwitch) {
              return props.row.block_lead
                ? `${(
                    (props.row.block_lead * 100) /
                    props.row.total_leads
                  ).toFixed(2)}%`
                : `0%`;
            }

            return props.row.block_lead ? props.row.block_lead : `0`;
          },
        },
        {
          slotName: 'bounce_lead',
          slotType: 'computed',
          classes: () => {
 	          return '';
          },
          value: (props) => {
            if (this.percentSwitch) {
              return props.row.bounce_lead
                ? `${(
                    (props.row.bounce_lead * 100) /
                    props.row.total_leads
                  ).toFixed(2)}%`
                : `0%`;
            }

            return props.row.bounce_lead ? props.row.bounce_lead : `0`;
          },
        },
        {
          slotName: 'suppression_lead',
          slotType: 'computed',
          classes: () => {
 	          return '';
          },
          value: (props) => {
            if (this.percentSwitch) {
              return props.row.suppression_lead
                ? `${(
                    (props.row.suppression_lead * 100) /
                    props.row.total_leads
                  ).toFixed(2)}%`
                : `0%`;
            }

            return props.row.suppression_lead
              ? props.row.suppression_lead
              : `0`;
          },
        },
        {
          slotName: 'good_leads',
          slotType: 'computed',
          classes: () => {
 	          return '';
          },
          value: (props) => {
            if (this.percentSwitch) {
              return props.row.good_leads
                ? `${(
                    (props.row.good_leads * 100) /
                    props.row.total_leads
                  ).toFixed(2)}%`
                : `0%`;
            }

            return props.row.good_leads ? props.row.good_leads : `0`;
          },
        },
        {
          slotName: 'created_at',
          slotType: 'computed',
          classes: () => {
 	          return '';
          },
          value: (props) => {
            return props.row.created_at
              ? `${new Date(props.row.created_at).getDate()} ${
                  this.months[new Date(props.row.created_at).getMonth()]
                }, ${new Date(props.row.created_at).getFullYear()}`
              : this.$t('frontend_groovemail_not_available');
          },
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          conditions: () => {
            return true;
          },
          classes: () => {
            return {
              outerDiv: '',
              dropdown: '',
              outerSpan: '',
            };
          },
          actionList: [
            {
              id: 0,
              name: () => this.$t(
                'frontend_groovemail_admin_import_user_approval_action_approve_all'
              ),
              conditions: () => {
                return this.importLeadType !== 1 && this.importLeadType !== 4;
              },
              callback: (props) => this.approveAllLeadsInCsv(props.row),
            },
            {
              id: 1,
              name: () => this.$t(
                'frontend_groovemail_admin_import_user_approval_action_approve_good'
              ),
              conditions: (props) => {
                return (
                  props.good_leads &&
                  this.importLeadType !== 1 &&
                  this.importLeadType !== 4
                );
              },
              callback: (props) => this.approveGoodLeadsInCsv(props.row),
            },
            {
              id: 2,
              name: () => this.$t(
                'frontend_groovemail_admin_import_user_approval_action_reject_all'
              ),
              conditions: () => {
                return (
                  this.importLeadType !== 3 &&
                  this.importLeadType !== 4
                );
              },
              callback: (props) => this.rejectLeadMethod(props.row,this.importLeadType),
            },
            {
              id: 3,
              name: () => this.$t(
                'frontend_groovemail_admin_import_user_approval_action_download_file'
              ),
              conditions: () => {
                return true;
              },
              callback: (props) =>
                this.downloadCsvOfLeads(props.row, this.downloadAllLeadsUrl, 2),
            },
            {
              id: 4,
              name: () => this.$t(
                'frontend_groovemail_admin_import_user_approval_action_download_good_leads_file'
              ),
              conditions: () => {
                return this.importLeadType !== 1 && this.importLeadType !== 4;
              },
              callback: (props) =>
                this.downloadCsvOfLeads(props.row, this.downloadAllGoodUrl, 1),
            },
            {
              id: 5,
              name: () => this.$t(
                'frontend_groovemail_admin_import_user_approval_action_download_bad_leads_file'
              ),
              conditions: () => {
                return this.importLeadType !== 1 && this.importLeadType !== 4;
              },
              callback: (props) =>
                this.downloadCsvOfLeads(props.row, this.downloadAllBadUrl, 0),
            },
          ],
        },
        {
          slotName: 'view',
          slotType: 'view',
          viewArray: [
            {
              id: 0,
              title:
                'frontend_groovemail_admin_import_user_approval_action_show_responses',
              conditions: () => {
                return true;
              },
              callback: (props) => {
                return this.showResponsesForThisCsv(props.row);
              },
              buttonType: 'icon',
              primary: true,
              buttonClass: 'fas fa-list-ol',
              classes: '',
            },
            {
              id: 1,
              title:
                'frontend_groovemail_admin_import_user_approval_action_show_field_mappings',
              conditions: () => {
                return true;
              },
              callback: (props) => {
                return this.showMappingsForThisCsv(props.row);
              },
              buttonType: 'icon',
              primary: true,
              buttonClass: 'fas fa-network-wired',
              classes: 'ml-2',
            },
            {
              id: 2,
              title: 'frontend_groovemail_import_admin_sender_and_gateway_info',
              conditions: (props) => {
                return (
                  this.importLeadType === 0 &&
                  props.row.senders &&
                  props.row.senders.length
                );
              },
              callback: (props) => {
                return this.showGatewaysForThisCsv(props.row);
              },
              buttonType: 'icon',
              primary: true,
              buttonClass: 'fas fa-users-cog',
              classes: 'ml-2',
            },
          ],
        },
        {
          slotName: 'reject_reason',
          slotType: 'tooltip',
          showText: false,
          textValue: () => {
            return '';
          },
          conditions: (props) => {
            return props.row.approved === 3;
          },
          content: (props) => {
            let tooltipContent = `<div class='flex'><p class='font-semibold break-words'>${this.$t(
              'frontend_groovemail_suppressions_enter_emails_unsubscribers_reason'
            )}:</p>`;

            if (props.row.reject_reason) {
              tooltipContent += `<p class='pl-1 break-words'>${props.row.reject_reason}</p></div>`;
            } else if (this.importLeadType !== 3) {
              tooltipContent += `<p class='pl-1 break-words'>${this.$t(
                'frontend_groovemail_import_user_listing_rejected_reason'
              )}</p></div>`;
            } else {
              tooltipContent += `<p class='pl-1 break-words'>${this.$t(
                'frontend_groovemail_not_available'
              )}</p></div>`;
            }

            return tooltipContent;
          },
          html: () =>{
            return `
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="text-danger h-4 w-4" fill="currentcolor">
                <path d="M12,0A12,12,0,1,0,24,12,12.013,12.013,0,0,0,12,0Zm0,22A10,10,0,1,1,22,12,10.011,10.011,0,0,1,12,22Z"/>
                <path d="M12,10H11a1,1,0,0,0,0,2h1v6a1,1,0,0,0,2,0V12A2,2,0,0,0,12,10Z"/>
                <circle cx="12" cy="6.5" r="1.5"/>
              </svg>
            `;
          }
        },
      ];
    },
  },

  methods: {
     

    // show loader
    showLoader() {
      this.$emit('showLoader');
    },

    // hide loader
    hideLoader (){
      this.$emit('hideLoader');
    },

    /**
     * Emits event to start downloading csv for contacts.
     * @param {object} csvData csv data
     * @param {string} url download url
     * @param {number} type type of contacts
     */
    downloadCsvOfLeads(csvData, url, type) {
      this.$emit('downloadCsvOfLeads', csvData, url, type);
    },
	
    /**
     * Emits event to reject csv of contacts.
     * @param {object} csvData csv data
     * @param {object} importStatus status
     */
    rejectLeadMethod(csvData, importStatus) {
      this.$emit('rejectLeadMethod', csvData,importStatus);
    },
	  
    /**
     * Emits event to approve all contacts from csv.
     * @param {object} csvData csv data
     */
    approveAllLeadsInCsv(csvData) {
      this.$emit('approveAllLeadsInCsv', csvData);
    },

	  /**
     * Emits event to approve good contacts from csv.
     * @param {object} csvData csv data
     */
    approveGoodLeadsInCsv(csvData) {
      this.$emit('approveGoodLeadsInCsv', csvData);
    },
	  
    /**
     * Emits event to show responses added along with csv of contacts.
     * @param {object} csvData csv data
     */
    showResponsesForThisCsv(csvData) {
      this.$emit('showResponsesForThisCsv', csvData);
    },
	
    /**
     * Emits event to show mapping of header columns of csv.
     * @param {object} csvData csv data
     */
    showMappingsForThisCsv(csvData) {
      this.$emit('showMappingsForThisCsv', csvData);
    },
	  
    /**
     * Emits event to show gateway along with thier sender
     * for the user who uploaded the csv of contacts.
     * @param {object} csvData csv data
     */
    showGatewaysForThisCsv(csvData) {
      this.$emit('showGatewaysForThisCsv', csvData);
    },
	
    /**
     * Emits event to show existing imports of the user.
     * @param {string} type type of files (approved or rejected)
     * @param {string} email email of the user
     */
    showImportsForThisUser(type, email) {
      this.$emit('showImportsForThisUser', type, email);
    },
  },
};
</script>